/**
 * @classdesc
 * Netx.Collections.SearchRules contains all the data to make up a DAM Search Bean, the argument to various API calls including searchAssetListObjects().
 * It is used heavily by Netx.Collections.Assets and its many subclasses, to define what assets should be fetched.
 * Its models describe a set of up to ten individual searches; multiple searches are combined
 * logically with "AND", "OR" or "NOT".  The collection also has eight search parameters, stored as non-Backbone
 * object properties. which affect the search as a whole.
 * Options for all of these fields are defined more completely in the object search/properties/search.
 *
 *     NOTE: The search parameter properties on the collection are manipulated via standard JS syntax, not via backbone's get() and set(), because this is a Collection, not a Model.
 *
 * @param {string}  sortField     Name of the field to sort results by.
 * @param {int}     sortOrder     0 == ascending order, 1 == descending order.
 * @param {int}     matchCriteria How to logically combine multiple type searches: 0 is "AND", 1 is "OR", 2 is "NOT".
 * @param {string}  saveSearch    If this string is not empty, the search will be saved under this name.
 * @param {int}     notifyType    If search is saved, how should the user be notified of updates to the search set?
 * @param {boolean} ignoreStat    Should this search be recorded against asset statistics?  true = no, false = yes.
 * @param {int}     startPosition Of the complete ordered set of search results, return results starting at this position.
 * @param {int}     maxItems      Of the complete ordered set of search results, return this many items or fewer.
 *
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/searchRule';

import Netx from '@netx/core/bootstrap';
import {dispatcher} from '@netx/core-next/instance/dispatcher';

import base64 from 'core-libs/base64';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

import {
	fileFormatFamilies,
	fileTypes,
	searchProperties,
} from '../properties/search';

export default class SearchRules extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * Methods for this model
	 * @type {object}
	 * @property {object} methods - Methods for this model
	 * @property {method} methods.read                - Get asset search bean for saved search
	 * @property {method} methods.fetchFileTypes      - We need to get this list of file types from the server at start up, for the use of Advanced Search, and put it in search/properties/search
	 * @property {method} methods.fetchFormatFamilies - We need to get this list of file format families from the server at start up, for the use of Advanced Search, and put it in search/properties/search
	 */
	get methods() {
		return {
			/**
			 * Fetch
			 * @method
			 */
			read: ['getAssetSearchBeanForSavedSearch', 'savedSearchId'],
			// We need to get this list of file types from the server at start up, for the use of Advanced Search, and put it in search/properties/search.
			// Rather than create a File Types collection just for that, I'm adding a method call here; the results are filed away by the parser, below.
			/**
			 * Get file types
			 * @method
			 */
			fetchFileTypes(opts) {
				return ['getChoiceList', 'file_type_list'];
			},
			/**
			 * Get format families
			 * @method
			 */
			fetchFormatFamilies(opts) {
				return ['getChoiceList', 'fileFormatFamilies'];
			},
		};
	}

	/*
	 * Parse the searchBean output of getAssetSearchBeanForSavedSearch into this object.
	 * @method
	 * @param {object} data Search rules data
	 * @return {object} PArsed search rules
	 */
	parse(data) {
		// if the data looks right:
		if (_.has(data, 'element1Type')) {
			const rxElement = /^element(\d+)(.*)/;
			const searchRules = [];

			for (let x = 0; x < 10; x++) {
				searchRules.push({});
			}

			_.each(data, (v, k, l) => {
				const match = k.match(rxElement);
				if (match) {
					const elIdx = match[1];
					const elPart = match[2];
					const idx = parseInt(elIdx) - 1;

					_.extend(searchRules[idx], {
						[`element${elPart}`]: v,
					});
				} else {
					// found a search property
					if (searchProperties.indexOf(k) > -1) {
						this[k] = v;
						return true;
					}
					console.warn(
						`unknown search element '${k}' from getAssetSearchBeanForSavedSearch()`,
					);
				}
			});

			// return just the rules that mean something
			return _.filter(searchRules, (r) => {
				return r.elementType > 0;
			});
		}
	}

	/**
	 * Parsers for this model
	 * @type {object}
	 * @property {object} parsers - Parsers for this model
	 * @property {object} parsers.fetchFileTypes      - File types fetched
	 * @property {object} parsers.fetchFormatFamilies - File format families fetched
	 */
	get parsers() {
		return {
			/**
			 * File types fetched
			 * @method
			 * @param {mixed} data Cart asset data
			 */
			fetchFileTypes(data) {
				_.each(data, (ft) => {
					// Filter out null strings and all-digit strings -- they are bad data introduced by DAM-1911; that bug is fixed but the bad data may persist.
					if (ft.label.length > 0 && !ft.label.match(/^\d+$/)) {
						fileTypes[ft.label] = ft.value;
					}
				});
			},
			/**
			 * File format families fetched
			 * @method
			 * @param {mixed} data Cart asset data
			 */
			fetchFormatFamilies(data) {
				_.each(data, (ff) => {
					if (ff.label.length > 0 && !ff.label.match(/^\d+$/)) {
						fileFormatFamilies[ff.label] = ff.value;
					}
				});
			},
		};
	}

	/**
	 * If a SearchRules object is initialized without any arguments, it will contain a single default SearchRule.
	 * If it is passed an array of N objects containing SearchRule attributes, N SearchRule models will be added.
	 * with those attributes set and all other attributes default.  An optional second argument can be
	 * an object specifying any subset of the eight general search parameters.
	 * @override
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * @type {number}
		 * @default
		 */
		this.matchCriteria = 0;

		/**
		 * Max items to fetch (0 is all)
		 * @type {number}
		 * @default
		 */
		this.maxItems = 0;

		/**
		 * sALO string map
		 * @memberOf Netx.Collections.SearchRules
		 * @property {object} sALOstrMap - sALO string map
		 * @property {string} sALOstrMap.a - Sort field
		 * @property {string} sALOstrMap.b - Sort order
		 * @property {string} sALOstrMap.c - Match criteria
		 * @property {string} sALOstrMap.d - Element types
		 * @property {string} sALOstrMap.e - Element sub type 1
		 * @property {string} sALOstrMap.f - Element sub type 2
		 * @property {string} sALOstrMap.g - Element value 1
		 * @property {string} sALOstrMap.h - Element value 2
		 * @property {string} sALOstrMap.i - Element value 3
		 * @property {string} sALOstrMap.j - Save search
		 * @property {string} sALOstrMap.k - Notify type
		 * @property {string} sALOstrMap.l - Ignore stat
		 * @property {string} sALOstrMap.m - Start position
		 * @property {string} sALOstrMap.n - Max items
		 */
		this.sALOstrMap = opts.sALOstrMap || {
			a: 'sortField',
			b: 'sortOrder',
			c: 'matchCriteria',
			d: 'elementTypes',
			e: 'elementSubType1s',
			f: 'elementSubType2s',
			g: 'elementValue1s',
			h: 'elementValue2s',
			i: 'elementValue3s',
			j: 'saveSearch',
			k: 'notifyType',
			l: 'ignoreStat',
			m: 'startPosition',
			n: 'maxItems',
		};

		/**
		 * For fetching a saved search from the DAM
		 * @type {number}
		 * @default
		 */
		this.savedSearchId = 0;

		/**
		 * The field to sort by
		 * @type {string}
		 * @default
		 */
		this.sortField = 'file';

		/**
		 * The sort order
		 * Ascending: 0, Descending: 1
		 * @type {number}
		 * @default
		 */
		this.sortOrder = 0;

		/**
		 * @type {number}
		 * @default
		 */
		this.startPosition = 1;

		// Set non-rule defaults.
		this.initProperties(opts);

		// If we are initialized without models, insert a default rule.
		if (!models) {
			this.add(new Model());
		}
		// else {
		// 	// TODO: This seems to be necessary, but I don't get why.  The parent constructor was handed 'objects' and should have
		// 	// turned it into objects, but it didn't.  Need to figure this out ... -m-
		// 	this.set( objects );
		// }
	}

	/**
	 * @method
	 * @param {string} option Option name
	 * @param {mixed} value Option value
	 * @param {object} opts Options
	 */
	changeOption(option, value, opts = {}) {
		switch (option) {
			case 'matchCriteria':
				value = parseInt(value);
				break;
		}

		const oldValue = this[option];
		this[option] = value;

		if (opts.silent && oldValue !== value) {
			this.trigger('change:' + option, this, value, opts).trigger(
				'change:option',
				this,
				option,
				value,
				opts,
			);
		}

		return this;
	}

	/**
	 * Aggregate the various arguments used by our search API calls
	 * (such as searchAssetListObjects(), aka "sALO"),
	 * into one handy object.
	 *
	 * @method
	 * @param {boolean} ignoreQuirks Ignore quirks
	 * @return {object} Asset search object
	 */
	createSearchObject(ignoreQuirks = false) {
		let elementTypes = [];
		let elementSubType1s = [];
		let elementSubType2s = [];
		let elementValue1s = [];
		let elementValue2s = [];
		let elementValue3s = [];

		this.forEach((model) => {
			const searchObject = model.getSearchObject(ignoreQuirks);
			const values = searchObject.elementValue1;

			// Either we care not about the quirks of our sALO params
			// or we do not have any multiple value search rules
			if (ignoreQuirks || !_.isArray(values)) {
				elementTypes.push(searchObject.elementType);
				elementSubType1s.push(searchObject.elementSubType1);
				elementSubType2s.push(searchObject.elementSubType2);
				elementValue1s.push(searchObject.elementValue1);
				elementValue2s.push(searchObject.elementValue2);
				elementValue3s.push(searchObject.elementValue3);
				return;
			}

			// Support for multiple value search rules
			// We must create a new rule in the sALO params for each
			// value passed on a rule
			const {
				elementType,
				elementSubType1,
				elementSubType2,
				elementValue2,
				elementValue3,
			} = searchObject;
			values.forEach((value) => {
				elementTypes.push(elementType);
				elementSubType1s.push(elementSubType1);
				elementSubType2s.push(elementSubType2);
				elementValue1s.push(value);
				elementValue2s.push(elementValue2);
				elementValue3s.push(elementValue3);
			});
		});

		return {
			sortField: this.sortField,
			sortOrder: this.sortOrder,
			matchCriteria: this.matchCriteria,
			elementTypes,
			elementSubType1s,
			elementSubType2s,
			elementValue1s,
			elementValue2s,
			elementValue3s,
			startPosition: this.startPosition,
			maxItems: this.maxItems,
		};
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	fetchSearchCriteria(opts) {
		return $.when(this.fetchFileTypes(opts), this.fetchFormatFamilies(opts));
	}

	/**
	 * Intialize the Asset Search Object Properties
	 * @method
	 * @param {object} opts Properties to set
	 */
	initProperties(opts = {}) {
		const userPrefKeys = this.userPrefKeys || {};

		let preferredSortOrder = parseInt(getPreference(userPrefKeys.sortOrder));
		if (isNaN(preferredSortOrder)) {
			preferredSortOrder = this.sortOrder;
		}

		_.extend(this, {
			// Asset attribute to sort on
			sortField:
				opts.sortField ||
				getPreference(userPrefKeys.sortField) ||
				this.sortField,
			// Ascending: 0, Descending: 1
			sortOrder: opts.sortOrder != null ? opts.sortOrder : preferredSortOrder,
			// Match ALL: 0, Match ANY: 1
			matchCriteria:
				opts.matchCriteria != null ? opts.matchCriteria : this.matchCriteria,
			// First asset in query to fetch
			startPosition:
				opts.startPosition != null ? opts.startPosition : this.startPosition,
			// Maximum number of items to return (zero == all items?)
			maxItems: opts.maxItems != null ? opts.maxItems : this.maxItems,
		});

		return this;
	}

	/**
	 * Non-sALO replacement for above strTosALO
	 * @method
	 * @param {string} str Base64 encoded search string
	 */
	loadFromStr(str) {
		let small = '';
		try {
			small = JSON.parse(base64.decode(str));
		} catch (e) {
			// a bogus URL did not decode into a legit object.
			dispatcher.trigger('app:alert', 'Search not found.', 'Info');
			return;
		}

		// clear out any existing rules.
		this.reset();
		this.initProperties();

		_.each(this.sALOstrMap, (realName, smallName) => {
			if (small[smallName] == null) return;

			// the elements of a search object are arrays with pluralized names 'element*s';
			// break those into individual searchRule properties, which are singular
			if (/^element.*s$/.test(realName)) {
				realName = realName.replace(/s$/, '');
				_.each(small[smallName], (val, idx) => {
					if (!this.at(idx)) {
						this.add(new this.model());
					}
					this.at(idx).set(realName, val);
				});
			} else {
				// unpack searchRules properties too:
				this[realName] = small[smallName];
			}
		});

		return this;
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchRules';
	}
}
